import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, HStack, Stack, Text, Menu, Spacer, SimpleGrid, Button, Circle } from "@chakra-ui/react";
import { useColorMode } from "../../components/ui/color-mode";
import AppAvatar from "components/utils/AppAvatar";
import {
  MdOutlineExitToApp,
  MdPersonOutline,
  MdOutlineLayers,
  MdOutlineDashboard,
  MdPalette,
  MdChevronRight,
} from "react-icons/md";
import { DASHBOARD_TINT_OPTIONS, useDashboardSettings } from "features/dashboard/settings/store";
import { getDashboardPath, getDashboardPortalLabel } from "features/dashboard/portal";
import { logOut } from "lib/auth";
import { resolveAvatarUrl } from "utils/avatar";

const menuContentProps = {
  bg: "dashboard.bg.popover",
  color: "dashboard.fg.default",
  borderColor: "dashboard.border.default",
};

const menuItemProps = {
  _highlighted: { bg: "dashboard.bg.surfaceMuted" },
};

const activeMenuItemProps = (isActive) =>
  isActive
    ? { ...menuItemProps, bg: "dashboard.bg.surfaceMuted", fontWeight: "semibold", _hover: { bg: "dashboard.bg.surfaceMuted" } }
    : menuItemProps;

const ProfileMenu = () => {
  const { setColorMode, colorMode } = useColorMode();
  const s = useDashboardSettings();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [loggingOut, setLoggingOut] = useState(false);

  const me = s.ready ? s.me : null;
  const instructorName = me?.instructor?.nombre?.trim();
  const displayName = instructorName || me?.correo || "—";
  const avatarSrc = resolveAvatarUrl(me?.instructor || me);
  const avatarSeed = me?.instructor?.id || me?.id || displayName;
  const subtitle = me?.instructor ? (me.instructor.rol?.trim() || "Instructor") : "Sin instructor";
  const tintOptions = s.options?.tintOptions ?? DASHBOARD_TINT_OPTIONS;
  const availablePortals = Array.isArray(s.availablePortals) ? s.availablePortals : [];
  const activePortal = s.portal || "club";
  const activeTint = s.dashboardTint || "gray";
  const isLightMode = colorMode === "light";
  const swatches = [{ value: "light", label: "light", isLight: true }, ...tintOptions.map((opt) => ({ ...opt, isLight: false }))];

  const isSwatchActive = (swatch) => {
    if (swatch.isLight) return isLightMode;
    return !isLightMode && swatch.value === activeTint;
  };

  const onSwatchSelect = (swatch) => {
    if (swatch.isLight) {
      setColorMode("light");
      return;
    }
    setColorMode("dark");
    s.setDashboardTint?.(swatch.value);
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);

    try {
      await logOut();
      s.reset?.();
      qc.clear();
      navigate("/", { replace: true });
    } catch (e) {
      console.error("Logout failed:", e);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <HStack gap="2" pl="2" cursor="pointer">
          <AppAvatar size="sm" name={displayName} src={avatarSrc} seed={avatarSeed} />
          <Stack gap="0">
            <Text fontWeight="medium" textStyle="sm" display={{ base: "none", md: "block" }}>
              {displayName}
            </Text>
            <Text color="fg.muted" textStyle="xs" display={{ base: "none", md: "block" }}>
              {subtitle}
            </Text>
          </Stack>
        </HStack>
      </Menu.Trigger>

      <Menu.Positioner>
        <Menu.Content {...menuContentProps}>
          <Menu.Item value="perfil" onClick={() => console.log("Perfil clicked")} {...menuItemProps}>
            <MdPersonOutline />
            <Box flex="1">Perfil</Box>
          </Menu.Item>

          <Menu.Root positioning={{ placement: "right-start", gutter: 2 }}>
            <Menu.TriggerItem value="vista" {...menuItemProps}>
              <MdOutlineLayers />
              <Box flex="1">Vista</Box>
              <Spacer />
              <Box color="dashboard.fg.muted">
                <MdChevronRight />
              </Box>
            </Menu.TriggerItem>

            <Menu.Positioner>
              <Menu.Content {...menuContentProps}>
                <Menu.Item value="instructor" {...menuItemProps}>Instructor</Menu.Item>
                <Menu.Item value="finanzas" {...menuItemProps}>Finanzas</Menu.Item>
                <Menu.Item value="inventario" {...menuItemProps}>Inventario</Menu.Item>
                <Menu.Item value="director" {...menuItemProps}>Director</Menu.Item>
                <Menu.Item value="dev" {...menuItemProps}>Web Dev</Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>

          <Menu.Root positioning={{ placement: "right-start", gutter: 2 }}>
            <Menu.TriggerItem value="portal" {...menuItemProps}>
              <MdOutlineDashboard />
              <Box flex="1">Portal</Box>
              <Spacer />
              <Box color="dashboard.fg.muted">
                <MdChevronRight />
              </Box>
            </Menu.TriggerItem>

            <Menu.Positioner>
              <Menu.Content {...menuContentProps}>
                {availablePortals.map((portal) => {
                  const isActive = portal === activePortal;
                  return (
                    <Menu.Item
                      key={portal}
                      value={portal}
                      onClick={() => navigate(getDashboardPath(portal))}
                      {...activeMenuItemProps(isActive)}
                    >
                      {getDashboardPortalLabel(portal)}
                    </Menu.Item>
                  );
                })}
                {availablePortals.length === 0 ? (
                  <Menu.Item value="none" disabled {...menuItemProps}>
                    Sin portales
                  </Menu.Item>
                ) : null}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>

          <Menu.Root positioning={{ placement: "right-start", gutter: 2 }}>
            <Menu.TriggerItem value="tinte" {...menuItemProps}>
              <MdPalette />
              <Box flex="1">Tema</Box>
              <Spacer />
              <Box color="dashboard.fg.muted">
                <MdChevronRight />
              </Box>
            </Menu.TriggerItem>

            <Menu.Positioner>
              <Menu.Content p="2" {...menuContentProps}>
                <SimpleGrid columns={3} gap="1">
                  {swatches.map((swatch) => {
                    const isActive = isSwatchActive(swatch);
                    return (
                      <Menu.Item
                        key={swatch.value}
                        value={swatch.value}
                        asChild
                        p="0"
                        rounded="md"
                        _focusVisible={{ outline: "none" }}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={swatch.label}
                          title={swatch.label}
                          minW="0"
                          h="10"
                          px="0"
                          onClick={() => onSwatchSelect(swatch)}
                          bg={isActive ? "dashboard.bg.surfaceMuted" : "transparent"}
                          _hover={{ bg: "dashboard.bg.surfaceSubtle" }}
                        >
                          {swatch.isLight ? (
                            <Circle
                              size="5"
                              bg="white"
                              borderWidth={isActive ? "2px" : "1px"}
                              borderColor={isActive ? "dashboard.accent.border" : "blackAlpha.300"}
                            />
                          ) : (
                            <Circle
                              size="5"
                              bg={`${swatch.value}.500`}
                              borderWidth={isActive ? "2px" : "1px"}
                              borderColor={isActive ? "white" : "blackAlpha.300"}
                            />
                          )}
                        </Button>
                      </Menu.Item>
                    );
                  })}
                </SimpleGrid>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>

          <Menu.Separator />

          <Menu.Item value="cerrar" onClick={handleLogout} disabled={loggingOut} {...menuItemProps}>
            <MdOutlineExitToApp />
            <Box flex="1">{loggingOut ? "Cerrando..." : "Cerrar Sesión"}</Box>
          </Menu.Item>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};

export default ProfileMenu;
